import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'

const CustomerAcquisitionInfoModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('dashboard')
  const dialogRef = useRef(null)
  const closeButtonRef = useRef(null)
  const previousFocusRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return undefined

    previousFocusRef.current = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const focusTimer = window.setTimeout(() => closeButtonRef.current?.focus(), 0)

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }

      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = dialogRef.current.querySelectorAll('button:not(:disabled)')
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      const focusIsInsideDialog = dialogRef.current.contains(document.activeElement)
      if (event.shiftKey && (document.activeElement === first || !focusIsInsideDialog)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (document.activeElement === last || !focusIsInsideDialog)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      previousFocusRef.current?.focus?.()
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-[#101828]/10 p-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <section
        ref={dialogRef}
        id="customer-acquisition-info-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-acquisition-info-title"
        aria-describedby="customer-acquisition-info-description"
        className="my-auto w-full max-w-[640px] rounded-[20px] border border-[#E4E7EC] bg-white p-6 shadow-[0_24px_72px_rgba(16,24,40,0.18)] sm:p-8"
      >
        <header className="flex items-center justify-between gap-4">
          <h2 id="customer-acquisition-info-title" className="text-2xl font-bold tracking-tight text-[#111827]">
            {t('customerAcquisitionInfoModal.title')}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label={t('customerAcquisitionInfoModal.close')}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#E4E7EC] bg-white text-[#667085] transition-colors hover:bg-[#F9FAFB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54]/30"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </header>

        <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-[#E1174C]">
          {t('customerAcquisitionInfoModal.category')}
        </p>
        <p id="customer-acquisition-info-description" className="mt-6 text-sm leading-6 text-[#475467]">
          {t('customerAcquisitionInfoModal.description')}
        </p>

        <section className="mt-6 rounded-2xl border border-[#EAECF0] bg-[#F9FAFB] px-4 py-5 sm:px-5">
          <p className="text-center text-[10px] font-bold uppercase tracking-[0.1em] text-[#F20D4F]">
            {t('customerAcquisitionInfoModal.formulaLabel')}
          </p>
          <div
            role="img"
            aria-label={t('customerAcquisitionInfoModal.formulaAccessible')}
            className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[#111827]"
          >
            <span aria-hidden="true" className="text-sm font-semibold sm:text-base">
              {t('customerAcquisitionInfoModal.conversionRate')} =
            </span>
            <span aria-hidden="true" className="inline-flex min-w-[160px] flex-col items-center font-serif text-lg leading-6 sm:min-w-[178px]">
              <span>{t('customerAcquisitionInfoModal.newCustomers')}</span>
              <span className="h-px w-full bg-[#111827]" />
              <span>{t('customerAcquisitionInfoModal.totalLeads')}</span>
            </span>
            <span aria-hidden="true" className="text-sm font-medium sm:text-base">× 100</span>
          </div>
        </section>

        <div className="mt-5 border-t border-[#EAECF0] pt-5">
          <p className="text-[13px] leading-5 text-[#475467]">
            {t('customerAcquisitionInfoModal.exampleCaption')}
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-3 rounded-2xl bg-[#FFF5F7] px-4 py-4 sm:gap-x-5 sm:px-5">
            <div
              role="img"
              aria-label={t('customerAcquisitionInfoModal.exampleAccessible')}
              className="flex items-center gap-3 text-[#111827]"
            >
              <span aria-hidden="true" className="inline-flex min-w-[84px] flex-col items-center font-serif text-lg leading-6">
                <span>26</span>
                <span className="h-px w-full bg-[#111827]" />
                <span>26 + 2</span>
              </span>
              <span aria-hidden="true" className="text-sm font-medium sm:text-base">× 100 =</span>
            </div>
            <div className="rounded-xl border border-[#FBC9D4] bg-white px-3.5 py-2">
              <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#E1174C]">
                {t('customerAcquisitionInfoModal.resultLabel')}
              </p>
              <p className="text-[25px] font-bold leading-[30px] tracking-tight text-[#E1174C]">
                {t('customerAcquisitionInfoModal.result')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default CustomerAcquisitionInfoModal
